import type { RouteMetric, ScenarioExposure, ScenarioOverride } from "./types";

const WEIGHT_PARAM = "weight";
const ROUTE_PARAM = "route";
const NO_DATA_ACTION = "Sign in and load live ArcGIS metrics before customizing this scenario.";

const RECOMMENDED_ACTIONS: Record<string, string> = {
  NORMAL: "Continue routine monitoring.",
  WATCH: "Watch the next refresh and check whether the top driver persists.",
  ELEVATED:
    "Flag affected shipments and notify the customer or account owner of potential Singapore transshipment delay exposure.",
  "CRITICAL EXPOSURE":
    "Escalate for commercial, customer, and claims-readiness review; do not treat as a payout trigger without validated trigger rules.",
  STALE: "Refresh source feeds before interpreting route exposure.",
  "No data": NO_DATA_ACTION
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function clampScenarioWeight(value: number): number {
  return clamp(value, 0, 1);
}

export function weightToPercent(weight: number | null): number | null {
  return weight === null ? null : Math.round(clampScenarioWeight(weight) * 100);
}

export function percentToWeight(percent: number): number {
  return clamp(percent, 0, 100) / 100;
}

export function categoryForRouteExposure(score: number): string {
  const bounded = clamp(score, 0, 100);
  if (bounded < 30) {
    return "NORMAL";
  }
  if (bounded < 60) {
    return "WATCH";
  }
  if (bounded < 80) {
    return "ELEVATED";
  }
  return "CRITICAL EXPOSURE";
}

export function recommendedActionForCategory(category: string): string {
  return RECOMMENDED_ACTIONS[category] ?? NO_DATA_ACTION;
}

function routeCanCalculate(route: RouteMetric): boolean {
  return route.congestionScore !== null && route.scoreStatus.toUpperCase() !== "STALE";
}

function defaultWeight(route: RouteMetric): number | null {
  return route.singaporeWeight === null ? null : clampScenarioWeight(route.singaporeWeight);
}

function roundedScore(score: number): number {
  return Math.round(clamp(score, 0, 100) * 10) / 10;
}

export function computeScenarioExposure(
  route: RouteMetric | null,
  override: ScenarioOverride
): ScenarioExposure {
  if (route === null) {
    return {
      baselineWeight: null,
      activeWeight: null,
      isOverride: false,
      score: null,
      category: "No data",
      recommendedAction: recommendedActionForCategory("No data"),
      baselineScore: null
    };
  }

  const baselineWeight = defaultWeight(route);
  const hasRouteOverride = override.routeId === route.routeId && override.singaporeWeight !== null;
  const activeWeight = hasRouteOverride ? clampScenarioWeight(override.singaporeWeight ?? 0) : baselineWeight;
  const isOverride = hasRouteOverride && baselineWeight !== null && activeWeight !== baselineWeight;

  if (!routeCanCalculate(route) || baselineWeight === null || activeWeight === null) {
    const category = route.scoreStatus.toUpperCase() === "STALE" ? "STALE" : route.delayExposureCategory || "No data";
    return {
      baselineWeight,
      activeWeight,
      isOverride,
      score: null,
      category,
      recommendedAction: recommendedActionForCategory(category),
      baselineScore: null
    };
  }

  const baselineScore = roundedScore((route.congestionScore ?? 0) * baselineWeight);
  const score = roundedScore((route.congestionScore ?? 0) * activeWeight);
  const category = categoryForRouteExposure(score);

  return {
    baselineWeight,
    activeWeight,
    isOverride,
    score,
    category,
    recommendedAction: recommendedActionForCategory(category),
    baselineScore
  };
}

function parseWeightParam(value: string | null): number | null {
  if (value === null || value.trim() === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? clampScenarioWeight(parsed) : null;
}

export function readScenarioState(search: string = window.location.search): ScenarioOverride {
  const params = new URLSearchParams(search);
  const routeId = params.get(ROUTE_PARAM);
  return {
    routeId: routeId === null || routeId.trim() === "" ? null : routeId,
    singaporeWeight: parseWeightParam(params.get(WEIGHT_PARAM))
  };
}

export function scenarioUrl(
  state: ScenarioOverride,
  href: string = window.location.href
): string {
  const url = new URL(href);
  if (state.routeId === null || state.routeId.trim() === "") {
    url.searchParams.delete(ROUTE_PARAM);
  } else {
    url.searchParams.set(ROUTE_PARAM, state.routeId);
  }

  if (state.singaporeWeight === null) {
    url.searchParams.delete(WEIGHT_PARAM);
  } else {
    url.searchParams.set(WEIGHT_PARAM, clampScenarioWeight(state.singaporeWeight).toFixed(2));
  }

  return url.toString();
}

export function writeScenarioState(state: ScenarioOverride): void {
  window.history.replaceState({}, "", scenarioUrl(state));
}
